import { get } from 'lodash';
import React, { Fragment, useMemo } from 'react';
import { Form, Input, Button, Segment, Header } from 'semantic-ui-react';
import { tradingApplications } from 'erisxkit/client';
import history from '../../constants/history';
import { getUserType } from '../../containers/MemberUsers/MemberUserSettings';
import * as userTypes from '../../constants/userTypes';
import { KYC, RESUBMIT_KYC } from '../../constants/userStates';
import Permissions from '../../containers/MemberUsers/Permissions';
import {
  API_KEY_PERMISSIONS_EXCLUDE_FROM_SELECT_ALL,
  API_KEY_PERMISSIONS_CONFIRM_HEADERS,
} from '../../constants/apiKeyPermissions';

const supportedTradingAppNames = ['erisx', 'cqg'];
const supportedTradingApplications = tradingApplications.filter((app) =>
  supportedTradingAppNames.includes(app.value),
);

const MemberSettings = ({
  user,
  handleChange,
  saveUserDetails,
  saveDisabled,
  update = {},
  userStates,
  appStates,
  employeeRoles,
  routeSubmittingOrdersToApi,
  userPermissionsStructure,
  memberUserTypes,
  resetMf,
  apiPermissionsStructure,
  currentUser,
}) => {
  const userType = useMemo(() => getUserType(user), [user]);
  const isMemberUser = useMemo(
    () => userType === userTypes.memberUser,
    [userType],
  );
  const isEmployee = useMemo(() => userType === userTypes.employee, [userType]);
  const isServiceAccount = useMemo(
    () => user.memberUserType === userTypes.serviceAccountUser,
    [user.memberUserType],
  );
  // Filter out Service Account option for non-service accounts
  const filteredMemberUserTypes = useMemo(() => {
    if (isServiceAccount) {
      // Service accounts see all options (but dropdown is disabled anyway)
      return memberUserTypes;
    }
    // Non-service accounts cannot select Service Account as an option
    return memberUserTypes.filter(
      (type) => type.value !== userTypes.serviceAccountUser,
    );
  }, [isServiceAccount, memberUserTypes]);
  // User is editing their own employee member user
  const isEditingCurrentEmployee = useMemo(() => {
    const isEditingSelf = user?.userId === currentUser?.userId;
    return isEmployee && isEditingSelf;
  }, [user, currentUser, isEmployee]);
  return (
    <Segment.Group>
      <Segment.Group horizontal>
        <Segment className="user-settings-segment">
          <Form>
            <Header as="h2" content="General" />
            {isMemberUser && (
              <Fragment>
                <Header as="h3"> Manual Onboarding </Header>
                <Form.Button
                  onClick={() => {
                    history.push(
                      `/member_users/${user.userId}/settings/onboard`,
                    );
                  }}
                >
                  Onboard
                </Form.Button>
              </Fragment>
            )}
            <Form.Field>
              <label>Email</label>
              <Input
                name="email"
                defaultValue={user.email}
                onChange={handleChange}
              />
            </Form.Field>
            <Form.Field>
              <label>Auth Id</label>
              <Input
                name="authId"
                defaultValue={user.authId}
                onChange={handleChange}
              />
            </Form.Field>
            {isEmployee && (
              <Fragment>
                <Form.Dropdown
                  label="Role"
                  name="roles"
                  multiple
                  fluid
                  search
                  selection
                  defaultValue={get(user, 'roles', [])}
                  options={employeeRoles.map((role) => ({
                    key: role,
                    value: role,
                    text: role,
                  }))}
                  onChange={handleChange}
                />
              </Fragment>
            )}
            {isMemberUser && (
              <Fragment>
                <Form.Field>
                  <label>Freshdesk Contact Id</label>
                  <Input
                    name="freshdeskContactId"
                    defaultValue={user.freshdeskContactId}
                    onChange={handleChange}
                  />
                </Form.Field>
                <Form.Dropdown
                  label="Member User Type"
                  name="memberUserType"
                  search
                  selection
                  defaultValue={user.memberUserType}
                  options={filteredMemberUserTypes}
                  onChange={handleChange}
                  disabled={isServiceAccount}
                />
                <Form.Dropdown
                  name="state"
                  label="User state"
                  fluid
                  search
                  selection
                  defaultValue={user.state}
                  options={userStates}
                  onChange={handleChange}
                />
              </Fragment>
            )}
            {[KYC, RESUBMIT_KYC].includes(get(user, 'state', '')) && (
              <Form.Dropdown
                name="appState"
                label="IDM App state"
                fluid
                search
                selection
                defaultValue={user.appState}
                options={appStates}
                onChange={handleChange}
              />
            )}
            <Button
              positive
              disabled={saveDisabled}
              id="save"
              onClick={saveUserDetails}
            >
              {' '}
              Save{' '}
            </Button>
          </Form>
        </Segment>
        {isEmployee && (
          <Segment className="user-settings-segment">
            <Form className="segment vertical">
              <Header as="h2" content="Security" />
              <Form.Button
                color="red"
                name="reset"
                onClick={resetMf}
                disabled={isEditingCurrentEmployee}
              >
                Reset 2FA
              </Form.Button>
            </Form>
          </Segment>
        )}
        {isMemberUser && (
          <Segment className="user-settings-segment">
            <Form className="segment vertical">
              <Header as="h2" content="Default Trading Application" />
              <Form.Dropdown
                defaultValue={user.defaultTradingApp}
                name="defaultTradingApp"
                onChange={handleChange}
                options={supportedTradingApplications.map(
                  ({ name, value, disabled }) => ({
                    key: name,
                    text: name,
                    value,
                    disabled,
                  }),
                )}
                selection
                search
              />
              <Form.Button
                disabled={!update.defaultTradingApp}
                color="green"
                content="Save"
                name="submitTradingApp"
                onClick={saveUserDetails}
              />
            </Form>
            <Form className="segment vertical">
              <Header as="h2" content="Security" />
              <Form.Button color="red" name="reset" onClick={resetMf}>
                Reset 2FA
              </Form.Button>
            </Form>
            <Form className="segment vertical">
              <Header as="h2" content="CQG" />
              <Form.Field>
                <label>CQG User Id</label>
                <Input
                  name="cqgUserId"
                  defaultValue={user.cqgUserId}
                  readOnly
                />
              </Form.Field>
              <Form.Field>
                <label>CQG Username</label>
                <Input
                  name="cqgUsername"
                  defaultValue={user.cqgUsername}
                  readOnly
                />
              </Form.Field>
              <Form.Field>
                <label>CQG Customer Id</label>
                <Input
                  name="cqgCustomerId"
                  defaultValue={user.cqgCustomerId}
                  readOnly
                />
              </Form.Field>
            </Form>
            <Form className="segment vertical">
              <Header as="h2" content="API Settings" />
              <Form.Button
                name="reset"
                onClick={() =>
                  routeSubmittingOrdersToApi({ memberUserId: user.userId })
                }
              >
                Route Fix Id for API Order Entry
              </Form.Button>
            </Form>
          </Segment>
        )}
      </Segment.Group>
      {isMemberUser && (
        <Segment.Group horizontal>
          <Segment className="user-settings-segment">
            <Permissions
              header="Member User Permissions"
              permissionsWithHeaderStructure={userPermissionsStructure}
              permissions={get(update, 'permissions', user?.permissions)}
              name="permissions"
              onChange={handleChange}
              dataWithHeader
              disabled={isServiceAccount}
            />
            <Button
              positive
              disabled={!update.permissions}
              id="savePermissions"
              onClick={saveUserDetails}
            >
              {' '}
              Save{' '}
            </Button>
          </Segment>
          <Segment className="user-settings-segment">
            <Permissions
              header="API Key Permissions"
              permissionsWithHeaderStructure={apiPermissionsStructure}
              permissions={get(
                update,
                'apiKeyPermissions',
                user?.apiKeyPermissions,
              )}
              name="apiKeyPermissions"
              onChange={handleChange}
              dataWithHeader
              excludeFromSelectAll={API_KEY_PERMISSIONS_EXCLUDE_FROM_SELECT_ALL}
              confirmHeaders={API_KEY_PERMISSIONS_CONFIRM_HEADERS}
            />
            <Button
              positive
              disabled={!update.apiKeyPermissions}
              id="saveApiKeyPermissions"
              onClick={saveUserDetails}
            >
              {' '}
              Save{' '}
            </Button>
          </Segment>
        </Segment.Group>
      )}
    </Segment.Group>
  );
};

export default MemberSettings;
